import React, { useContext } from 'react';

import { useMutation } from '@apollo/client';
import {
  REMOVE_NEW_TAG_FROM_ARTICLE_MUTATION,
  REMOVE_NEW_TAG_FROM_EVENT_MUTATION,
} from 'Queries/Mutations';
import classnames from 'classnames';
import { ParticipantsListContext } from 'context/ParticipantsList';
import { array, bool, string } from 'prop-types';
import { withRouter } from 'react-router';
import Truncate from 'react-truncate';

import { ActionButton } from 'components/ActionButton/ActionButton';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import { OuterBox } from 'components/OuterBox/OuterBox';
import { Separator } from 'components/Separator/Separator';
import { SubTitle } from 'components/SubTitle/SubTitle';
import { Tags } from 'components/Tags/Tags';

import { OVERVIEW_LIST_TYPES, TENANT_ID } from 'global/constants';

import { formatDate } from 'utils/DatesUtils';
import { getTenantId } from 'utils/tenantUtil';

import { isNewEntry } from './utils/isNewEntry';

import './OverviewList.scss';

const STATUS_TAGS = new Set(['Neu', 'Update']);
const MAX_TAGS = 3;

const getDisplayTags = (isNew, tags = []) => {
  const contentTags = tags.filter((tag) => !STATUS_TAGS.has(tag));
  const displayTags = isNew ? ['Neu', ...contentTags] : contentTags;

  return displayTags.slice(0, MAX_TAGS);
};

export const OverviewList = withRouter(
  ({
    history,
    headline,
    isLoading,
    listData = [],
    className,
    listType,
    buttonLabel,
    buttonLink,
  }) => {
    const listClasses = classnames('list', {
      [`${className}`]: !!className,
    });

    const { setList } = useContext(ParticipantsListContext);

    const [removeNewTagArticle] = useMutation(REMOVE_NEW_TAG_FROM_ARTICLE_MUTATION);
    const [removeNewTagEvents] = useMutation(REMOVE_NEW_TAG_FROM_EVENT_MUTATION);

    const handleClick = async (e, { path, id }) => {
      setList(false);
      if (!id || !path) return;

      if (listType === OVERVIEW_LIST_TYPES.article) {
        await removeNewTagArticle({
          variables: {
            articleId: id,
          },
        });
      }

      if (listType === OVERVIEW_LIST_TYPES.event) {
        await removeNewTagEvents({
          variables: {
            eventId: id,
          },
        });
      }

      e.preventDefault();
      if (path.charAt(0) !== '/') path = `/${path}`;
      history.push(path);
    };

    const getDateString = (date) => (date ? `${formatDate(date)}` : null);

    return (
      <div className={listClasses}>
        <OuterBox>
          <SubTitle text={headline} notFramed />
          <Separator color={getTenantId() === TENANT_ID.AVA ? 'homepage' : 'secondary'} />
          {isLoading && <LoadingIndicator className="list-loading-indicator" />}
          {!isLoading && (
            <div className="list-group">
              {listData.map((listItem, index) => {
                const { id, creationDate, tags, title, path, startDate } = listItem;

                const isNew = isNewEntry(tags);
                const displayTags = getDisplayTags(isNew, tags);
                const listItemClasses = classnames('list-group-item', {
                  'list-group-item--new': isNew,
                });

                const dateInfo =
                  listType === OVERVIEW_LIST_TYPES.event
                    ? getDateString(startDate)
                    : getDateString(creationDate);

                return (
                  <button
                    key={path}
                    tabIndex={index}
                    onClick={(e) => handleClick(e, { path, id })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleClick(e, { path, id });
                      }
                    }}
                    className="overview-list-item">
                    <div key={listItem.id} className={listItemClasses}>
                      <div className="list-group-item__excerpt">
                        {displayTags.length > 0 && (
                          <div className="list-group-item__tags-row">
                            <Tags tags={displayTags} inverted={isNew} />
                          </div>
                        )}
                        <div className="list-group-item__title">
                          <Truncate lines={1} ellipsis="">
                            {title}
                          </Truncate>
                        </div>
                      </div>
                      <div className="list-group-item__meta-data">
                        {dateInfo && <div className="list-group-item__date">{dateInfo}</div>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          <ActionButton link={buttonLink} title={buttonLabel} inverted />
        </OuterBox>
      </div>
    );
  }
);

OverviewList.defaultProps = {
  listType: OVERVIEW_LIST_TYPES.event,
};

OverviewList.propTypes = {
  headline: string,
  className: string,
  listType: string,
  listData: array,
  buttonLabel: string.isRequired,
  buttonLink: string.isRequired,
  isLoading: bool,
};

OverviewList.defaultProps = {
  headline: '',
  className: '',
  listData: [],
  isLoading: false,
};
